<template>
  <div class="add-event-container">
    <div class="header-actions">
      <button class="btn-back" @click="goBack">
        <font-awesome-icon icon="arrow-left" /> Back to Events
      </button>
    </div>

    <h1>Add New Event</h1>
    <p class="calendar-name">Calendar: {{ calendarName }}</p>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <form @submit.prevent="submitForm" class="event-form">
      <!-- Basic Information -->
      <div class="form-section">
        <h2>Basic Information</h2>

        <div class="form-group">
          <label for="title">Event Title *</label>
          <input
            type="text"
            id="title"
            v-model="formData.title"
            required
            placeholder="Enter event title"
          />
        </div>

        <div class="form-group">
          <label for="type">Event Type *</label>
          <select id="type" v-model="formData.type" required>
            <option value="">Select Event Type</option>
            <option value="closed">Closed</option>
            <option value="early">Early</option>
            <option value="shortened">Shortened</option>
            <option value="late_start">Late Start</option>
          </select>
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="formData.description"
            placeholder="Enter event description"
            rows="3"
          ></textarea>
        </div>
      </div>

      <!-- Date and Time -->
      <div class="form-section">
        <h2>Date and Time</h2>

        <div class="form-row">
          <div class="form-group">
            <label for="startDate">Start Date *</label>
            <input
              type="date"
              id="startDate"
              v-model="formData.startDate"
              required
              :min="calendarStartDate"
              :max="calendarEndDate"
            />
          </div>

          <div class="form-group">
            <label for="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              v-model="formData.endDate"
              :min="formData.startDate"
              :max="calendarEndDate"
            />
            <small>Leave blank for single-day events</small>
          </div>
        </div>

        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="isAllDay"
            v-model="formData.isAllDay"
            @change="handleAllDayChange"
          />
          <label for="isAllDay">All Day Event</label>
        </div>

        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="isAllWeek"
            v-model="formData.isAllWeek"
            @change="handleAllWeekChange"
          />
          <label for="isAllWeek">All Week Event</label>
        </div>

        <div v-if="!formData.isAllDay && !formData.isAllWeek" class="form-row">
          <div class="form-group">
            <label for="startTime">Start Time</label>
            <input type="time" id="startTime" v-model="formData.startTime" />
          </div>

          <div class="form-group">
            <label for="endTime">End Time</label>
            <input type="time" id="endTime" v-model="formData.endTime" />
          </div>
        </div>

        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="affectsAttendance"
            v-model="formData.affectsAttendance"
          />
          <label for="affectsAttendance">This event affects attendance</label>
        </div>
      </div>

      <!-- Grade Ranges -->
      <div class="form-section">
        <h2>Grade Ranges</h2>
        <p class="help-text">Select which grade levels this event applies to</p>

        <div class="grade-ranges">
          <div class="form-group checkbox-group">
            <input
              type="checkbox"
              id="allGrades"
              :checked="formData.gradeRanges.includes('all_grades')"
              @change="handleAllGradesChange"
            />
            <label for="allGrades">All Grades</label>
          </div>

          <div class="form-group checkbox-group">
            <input
              type="checkbox"
              id="elementary"
              :checked="formData.gradeRanges.includes('elementary')"
              @change="handleGradeRangeChange('elementary', $event)"
              :disabled="formData.gradeRanges.includes('all_grades')"
            />
            <label for="elementary">Elementary (PreK-5)</label>
          </div>

          <div class="form-group checkbox-group">
            <input
              type="checkbox"
              id="middle"
              :checked="formData.gradeRanges.includes('middle')"
              @change="handleGradeRangeChange('middle', $event)"
              :disabled="formData.gradeRanges.includes('all_grades')"
            />
            <label for="middle">Middle School (6-8)</label>
          </div>

          <div class="form-group checkbox-group">
            <input
              type="checkbox"
              id="high"
              :checked="formData.gradeRanges.includes('high')"
              @change="handleGradeRangeChange('high', $event)"
              :disabled="formData.gradeRanges.includes('all_grades')"
            />
            <label for="high">High School (9-12)</label>
          </div>
        </div>
      </div>

      <!-- Recurring Options -->
      <div class="form-section">
        <h2>Recurring Options</h2>

        <div class="form-group checkbox-group">
          <input
            type="checkbox"
            id="isRecurring"
            v-model="formData.isRecurring"
          />
          <label for="isRecurring">This is a recurring event</label>
        </div>

        <div v-if="formData.isRecurring" class="recurring-options">
          <!-- Recurrence Preset Dropdown -->
          <div class="form-group">
            <label for="recurrencePreset">Recurrence Preset</label>
            <select
              id="recurrencePreset"
              v-model="recurrencePreset"
              @change="applyRecurrencePreset"
            >
              <option value="">Custom</option>
              <option value="every_wed">Every Wednesday</option>
              <option value="every_other_wed">Every Other Wednesday</option>
              <option value="every_weekday">Every Weekday (Mon–Fri)</option>
              <option value="mwf">Every Mon, Wed, Fri</option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group" style="max-width: 120px">
              <label for="recurrenceInterval">Repeat Every</label>
              <input
                id="recurrenceInterval"
                type="number"
                min="1"
                v-model.number="formData.recurrenceInterval"
              />
            </div>
            <div class="form-group" style="max-width: 160px">
              <label for="recurrenceUnit">&nbsp;</label>
              <select id="recurrenceUnit" v-model="formData.recurrenceUnit">
                <option value="day">Day(s)</option>
                <option value="week">Week(s)</option>
                <option value="month">Month(s)</option>
                <option value="year">Year(s)</option>
              </select>
            </div>
          </div>

          <!-- Weekly: Pick days -->
          <div v-if="formData.recurrenceUnit === 'week'" class="form-group">
            <label>On</label>
            <div class="weekday-picker">
              <button
                v-for="day in weekdays"
                :key="day.value"
                type="button"
                :class="[
                  'weekday-btn',
                  { selected: formData.recurrenceDays.includes(day.value) },
                ]"
                @click="toggleRecurrenceDay(day.value)"
              >
                {{ day.label }}
              </button>
            </div>
          </div>

          <!-- Monthly: Day of month or Nth weekday -->
          <div v-if="formData.recurrenceUnit === 'month'" class="form-group">
            <label>Repeat On</label>
            <div style="display: flex; gap: 12px; align-items: center">
              <label>
                <input
                  type="radio"
                  value="day"
                  v-model="formData.monthlyRepeatType"
                />
                Day {{ getDayOfMonth(formData.startDate) }}
              </label>
              <label>
                <input
                  type="radio"
                  value="weekday"
                  v-model="formData.monthlyRepeatType"
                />
                {{ getMonthlyWeekdayText(formData.startDate) }}
              </label>
            </div>
          </div>

          <!-- End condition -->
          <div class="form-group">
            <label>Ends</label>
            <div
              style="
                display: flex;
                gap: 16px;
                align-items: center;
                flex-wrap: wrap;
              "
            >
              <label>
                <input
                  type="radio"
                  value="never"
                  v-model="formData.recurrenceEndType"
                />
                Never
              </label>
              <label>
                <input
                  type="radio"
                  value="after"
                  v-model="formData.recurrenceEndType"
                />
                After
                <input
                  type="number"
                  min="1"
                  style="width: 60px; margin: 0 4px"
                  v-model.number="formData.recurrenceEndAfter"
                  :disabled="formData.recurrenceEndType !== 'after'"
                />
                occurrences
              </label>
              <label>
                <input
                  type="radio"
                  value="on"
                  v-model="formData.recurrenceEndType"
                />
                On
                <input
                  type="date"
                  v-model="formData.recurrenceEndDate"
                  :min="formData.startDate"
                  :disabled="formData.recurrenceEndType !== 'on'"
                />
              </label>
            </div>
          </div>

          <!-- Exclusion dates -->
          <div class="form-group">
            <label>Exclusion Dates</label>
            <div
              style="
                display: flex;
                gap: 8px;
                align-items: center;
                margin-bottom: 6px;
              "
            >
              <input
                type="date"
                v-model="exclusionDateInput"
                :min="formData.startDate"
              />
              <button
                type="button"
                class="btn-cancel"
                style="padding: 4px 10px; font-size: 0.95rem"
                @click="addExclusionDate"
              >
                Add
              </button>
            </div>
            <div v-if="formData.recurrenceExclusions.length">
              <span
                v-for="(date, i) in formData.recurrenceExclusions"
                :key="date"
                style="
                  display: inline-block;
                  background: #f3f4f6;
                  color: #374151;
                  border-radius: 4px;
                  padding: 2px 8px;
                  margin-right: 6px;
                  margin-bottom: 4px;
                  font-size: 0.97rem;
                "
              >
                {{ date }}
                <button
                  type="button"
                  @click="removeExclusionDate(i)"
                  style="
                    background: none;
                    border: none;
                    color: #b91c1c;
                    font-weight: bold;
                    margin-left: 4px;
                    cursor: pointer;
                  "
                >
                  ×
                </button>
              </span>
            </div>
          </div>
        </div>
        <!-- Recurrence Preview -->
        <div
          v-if="formData.isRecurring"
          class="form-group"
          style="margin-top: 18px"
        >
          <label>Preview Dates</label>
          <div v-if="previewDates.length">
            <ul
              style="
                max-height: 120px;
                overflow-y: auto;
                background: #f9fafb;
                border-radius: 6px;
                padding: 8px 12px;
                border: 1px solid #e5e7eb;
                font-size: 0.98rem;
              "
            >
              <li v-for="date in previewDates" :key="date">{{ date }}</li>
            </ul>
            <div
              v-if="previewDates.length > 20"
              style="color: #888; font-size: 0.95rem; margin-top: 4px"
            >
              ...and {{ previewDates.length - 20 }} more
            </div>
          </div>
          <div v-else style="color: #b91c1c; font-size: 0.98rem">
            No dates generated. Please check your recurrence settings.
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn-cancel" @click="goBack">Cancel</button>
        <button type="submit" class="btn-submit" :disabled="isSubmitting">
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Save Event</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import { API_URL } from "../config";
import { getErrorMessage } from "../types";
import type { EventFormData, GeneratedCalendarEvent } from "../types";

// Router and Route
const router = useRouter();
const route = useRoute();
const calendarId = route.params.calendarId as string;

// State
const calendarName = ref("");
const calendarStartDate = ref("");
const calendarEndDate = ref("");
const error = ref("");
const isSubmitting = ref(false);

const weekdays = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 0 },
];

// Form data
const formData = reactive<EventFormData>({
  title: "",
  type: "",
  description: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  isAllDay: false,
  isAllWeek: false,
  isRecurring: false,
  recurrenceInterval: 1,
  recurrenceUnit: "week",
  recurrenceDays: [3], // Default to Wednesday
  monthlyRepeatType: "day", // 'day' or 'weekday'
  recurrenceEndType: "never", // 'never', 'after', 'on'
  recurrenceEndAfter: 10,
  recurrenceEndDate: "",
  recurrenceExclusions: [] as string[],
  affectsAttendance: true,
  gradeRanges: ["all_grades"] as string[],
});

const exclusionDateInput = ref("");
const recurrencePreset = ref("");

const previewDates = computed(() => {
  // Use the same logic as generateEvents, but just return the date strings (limit to 20 for preview)
  const eventData = { ...formData };
  const events = generateEvents(eventData);
  return events.slice(0, 20).map((e) => e.startDate);
});

// Functions
const fetchCalendar = async () => {
  try {
    const response = await axios.get(`${API_URL}/calendar/${calendarId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const calendar = response.data;
    calendarName.value = calendar.name;
    calendarStartDate.value = calendar.startDate;
    calendarEndDate.value = calendar.endDate;

    // Set default start date to today if within calendar range
    const today = new Date().toISOString().split("T")[0];
    if (today >= calendar.startDate && today <= calendar.endDate) {
      formData.startDate = today;
    } else {
      formData.startDate = calendar.startDate;
    }

    // Set default times if available
    if (calendar.defaultStartTime) {
      formData.startTime = calendar.defaultStartTime;
    }
    if (calendar.defaultEndTime) {
      formData.endTime = calendar.defaultEndTime;
    }
  } catch (err: unknown) {
    error.value = getErrorMessage(err, "Failed to fetch calendar");
    console.error("Error fetching calendar:", err);
  }
};

// Handle all day change
const handleAllDayChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  if (isChecked) {
    formData.startTime = "";
    formData.endTime = "";
    formData.isAllWeek = false;
  }
};

// Handle all week change
const handleAllWeekChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  if (isChecked) {
    formData.startTime = "";
    formData.endTime = "";
    formData.isAllDay = false;
    // Set end date to end of week
    const startDate = new Date(formData.startDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + (6 - startDate.getDay()));
    formData.endDate = endDate.toISOString().split("T")[0];
  } else {
    formData.endDate = "";
  }
};

// Generate events for multi-day and recurring events
const generateEvents = (eventData: EventFormData): GeneratedCalendarEvent[] => {
  // If not recurring, fallback to single/multi-day logic
  if (!eventData.isRecurring) {
    if (!eventData.endDate || eventData.endDate === eventData.startDate) {
      return [eventData];
    }
    const events: GeneratedCalendarEvent[] = [];
    const startDate = new Date(eventData.startDate);
    const endDate = new Date(eventData.endDate);
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const event = {
        ...eventData,
        startDate: currentDate.toISOString().split("T")[0],
        endDate: currentDate.toISOString().split("T")[0],
      };
      events.push(event);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return events;
  }

  // Recurring logic
  const {
    startDate,
    recurrenceInterval,
    recurrenceUnit,
    recurrenceDays,
    recurrenceEndDate,
  } = eventData;
  const events: GeneratedCalendarEvent[] = [];
  let current = new Date(startDate);
  const end = new Date(recurrenceEndDate || startDate);

  // Helper for day of week (0=Sun, 1=Mon, ... 6=Sat)
  const getDay = (date: Date) => date.getDay();

  if (recurrenceUnit === "day") {
    while (current <= end) {
      events.push({
        ...eventData,
        startDate: current.toISOString().split("T")[0],
        endDate: current.toISOString().split("T")[0],
      });
      current.setDate(current.getDate() + recurrenceInterval);
    }
  } else if (recurrenceUnit === "week") {
    // Find the first week that matches
    let weekStart = new Date(current);
    while (weekStart <= end) {
      // For each selected day in the week
      for (const day of recurrenceDays) {
        // Calculate the date for this day in the current week
        const eventDate = new Date(weekStart);
        // Adjust to the correct day of week
        eventDate.setDate(weekStart.getDate() - getDay(weekStart) + day);
        // Only add if in range
        if (eventDate >= new Date(startDate) && eventDate <= end) {
          events.push({
            ...eventData,
            startDate: eventDate.toISOString().split("T")[0],
            endDate: eventDate.toISOString().split("T")[0],
          });
        }
      }
      // Move to next interval week
      weekStart.setDate(weekStart.getDate() + 7 * recurrenceInterval);
    }
  } else if (recurrenceUnit === "month") {
    while (current <= end) {
      events.push({
        ...eventData,
        startDate: current.toISOString().split("T")[0],
        endDate: current.toISOString().split("T")[0],
      });
      current.setMonth(current.getMonth() + recurrenceInterval);
    }
  } else if (recurrenceUnit === "year") {
    while (current <= end) {
      events.push({
        ...eventData,
        startDate: current.toISOString().split("T")[0],
        endDate: current.toISOString().split("T")[0],
      });
      current.setFullYear(current.getFullYear() + recurrenceInterval);
    }
  }
  return events;
};

const submitForm = async () => {
  // Validate form
  if (!formData.title) {
    error.value = "Please enter an event title";
    return;
  }

  if (!formData.type) {
    error.value = "Please select an event type";
    return;
  }

  if (!formData.startDate) {
    error.value = "Please select a start date";
    return;
  }

  isSubmitting.value = true;
  error.value = "";

  try {
    // Prepare data for submission
    const eventData = {
      ...formData,
    };

    // Generate events for multi-day and recurring events
    const events = generateEvents(eventData);

    // Create all events
    for (const event of events) {
      await axios.post(`${API_URL}/calendar/${calendarId}/events`, event, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    }

    // Navigate back to events list
    router.push(`/calendar/${calendarId}/events`);
  } catch (err: unknown) {
    isSubmitting.value = false;
    error.value = getErrorMessage(err, "Failed to create event");
    console.error("Error creating event:", err);
  }
};

const goBack = () => {
  router.push(`/calendar/${calendarId}/events`);
};

function toggleRecurrenceDay(day: number) {
  const idx = formData.recurrenceDays.indexOf(day);
  if (idx === -1) formData.recurrenceDays.push(day);
  else formData.recurrenceDays.splice(idx, 1);
}

function addExclusionDate() {
  if (
    exclusionDateInput.value &&
    !formData.recurrenceExclusions.includes(exclusionDateInput.value)
  ) {
    formData.recurrenceExclusions.push(exclusionDateInput.value);
    exclusionDateInput.value = "";
  }
}
function removeExclusionDate(idx: number) {
  formData.recurrenceExclusions.splice(idx, 1);
}

function getDayOfMonth(dateStr: string) {
  if (!dateStr) return "";
  return new Date(dateStr).getDate();
}
function getMonthlyWeekdayText(dateStr: string) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const weekday = weekdays[date.getDay()].label;
  const nth = Math.ceil(date.getDate() / 7);
  const nthText = ["1st", "2nd", "3rd", "4th", "5th"][nth - 1] || `${nth}th`;
  return `the ${nthText} ${weekday}`;
}

function applyRecurrencePreset() {
  if (recurrencePreset.value === "every_wed") {
    formData.recurrenceInterval = 1;
    formData.recurrenceUnit = "week";
    formData.recurrenceDays = [3];
  } else if (recurrencePreset.value === "every_other_wed") {
    formData.recurrenceInterval = 2;
    formData.recurrenceUnit = "week";
    formData.recurrenceDays = [3];
  } else if (recurrencePreset.value === "every_weekday") {
    formData.recurrenceInterval = 1;
    formData.recurrenceUnit = "week";
    formData.recurrenceDays = [1, 2, 3, 4, 5];
  } else if (recurrencePreset.value === "mwf") {
    formData.recurrenceInterval = 1;
    formData.recurrenceUnit = "week";
    formData.recurrenceDays = [1, 3, 5];
  }
  // If custom, do nothing (user can use advanced options)
}

// Update the handlers in the script section
const handleAllGradesChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  console.log("All grades checkbox changed:", isChecked);

  if (isChecked) {
    formData.gradeRanges = ["all_grades"];
  } else {
    // When unchecking "All Grades", default to elementary
    formData.gradeRanges = ["elementary"];
  }

  console.log("Updated grade ranges:", formData.gradeRanges);
};

const handleGradeRangeChange = (grade: string, event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;
  console.log(`${grade} checkbox changed:`, isChecked);

  if (isChecked) {
    // Add the grade if it's not already in the array
    if (!formData.gradeRanges.includes(grade)) {
      formData.gradeRanges.push(grade);
    }
  } else {
    // Remove the grade from the array
    formData.gradeRanges = formData.gradeRanges.filter((g) => g !== grade);

    // If no grades are selected, default to all grades
    if (formData.gradeRanges.length === 0) {
      formData.gradeRanges = ["all_grades"];
    }
  }

  console.log("Updated grade ranges:", formData.gradeRanges);
};

// Lifecycle hooks
onMounted(() => {
  fetchCalendar();
});
</script>

<style scoped>
body,
.add-event-bg {
  background: #f5f7fa;
}

.add-event-container {
  max-width: 1000px;
  margin-top: 40px;
  margin-left: 0;
  padding-left: 32px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 6px 32px rgba(0, 0, 0, 0.1);
  padding: 36px 32px 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.header-actions {
  margin-bottom: 18px;
}

.btn-back {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 15px;
  transition: background 0.15s;
}

.btn-back:hover {
  background-color: #e5e7eb;
}

h1 {
  margin-bottom: 4px;
  color: #1a202c;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -1px;
}

.calendar-name {
  margin-bottom: 22px;
  color: #6b7280;
  font-style: italic;
  font-size: 1.05rem;
}

.error-message {
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 18px;
  font-size: 1rem;
  border: 1px solid #fecaca;
}

.event-form {
  background: none;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.form-section {
  padding: 0;
  margin-bottom: 22px;
}

.form-section:not(:last-child) {
  border-bottom: 1px solid #f1f1f1;
  padding-bottom: 18px;
}

.form-section h2 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #1e293b;
  font-size: 1.13rem;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.form-group {
  margin-bottom: 14px;
}

.form-row {
  display: flex;
  gap: 18px;
}

.form-row .form-group {
  flex: 1;
}

@media (max-width: 700px) {
  .add-event-container {
    max-width: 98vw;
    margin: 10px 1vw;
    padding: 16px 6vw;
  }
  .form-row {
    flex-direction: column;
    gap: 8px;
  }
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #334155;
  font-size: 1rem;
}

input[type="text"],
input[type="date"],
input[type="time"],
select,
textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1rem;
  background: #f9fafb;
  transition: border 0.15s;
}
input:focus,
select:focus,
textarea:focus {
  border: 1.5px solid #2563eb;
  outline: none;
  background: #fff;
}

textarea {
  resize: vertical;
  min-height: 70px;
}

small {
  display: block;
  margin-top: 3px;
  color: #888;
  font-size: 12px;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  margin: 0;
  accent-color: #2563eb;
}

.checkbox-group label {
  margin: 0;
  font-size: 1rem;
  color: #374151;
}

.recurring-options {
  margin-top: 10px;
  padding: 12px;
  background-color: #f3f4f6;
  border-radius: 6px;
}

.help-text {
  margin-top: 0;
  margin-bottom: 10px;
  color: #666;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0;
  margin-top: 10px;
}

.btn-cancel,
.btn-submit {
  padding: 10px 28px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 700;
  font-size: 1.08rem;
  transition: background 0.15s, color 0.15s;
}

.btn-cancel {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-cancel:hover {
  background-color: #e5e7eb;
}

.btn-submit {
  background-color: #2563eb;
  color: white;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
}

.btn-submit:hover {
  background-color: #1d4ed8;
}

.btn-submit:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .add-event-container {
    padding: 8px 2vw;
  }
  .form-actions {
    flex-direction: column;
    gap: 8px;
  }
  .btn-cancel,
  .btn-submit {
    width: 100%;
  }
}

.weekday-picker {
  display: flex;
  gap: 6px;
  margin-top: 2px;
}
.weekday-btn {
  padding: 5px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 5px;
  background: #f9fafb;
  color: #374151;
  font-size: 0.98rem;
  cursor: pointer;
  transition: background 0.15s, border 0.15s;
}
.weekday-btn.selected {
  background: #2563eb;
  color: #fff;
  border: 1.5px solid #2563eb;
}

.grade-ranges {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.grade-ranges .checkbox-group {
  margin-bottom: 0;
}

.grade-ranges .checkbox-group label {
  font-size: 1rem;
  color: #374151;
}

.grade-ranges .checkbox-group input[type="checkbox"]:disabled + label {
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
